/**
 * Portal Manta · app-globals.js
 * Símbolos top-level do App.jsx referenciados pelos arquivos extraídos.
 * Copiados literalmente do App.jsx para evitar dependência circular.
 */
import { PAL_DEV } from './data/pal-dev.js';
import { SP_API } from './data/sp-api.js';

// ═══════════════════════════════════════════════
// API CLIENT (App.jsx:258–294)
// ═══════════════════════════════════════════════
export const API_BASE = (typeof window !== 'undefined' && window.MANTA_API_BASE) || 'http://localhost:3001';
export const API = {
  base: API_BASE,
  enabled: false,
  async ping() {
    try {
      const r = await fetch(API_BASE + '/api/health', { cache: 'no-store' });
      if (!r.ok) return null;
      const j = await r.json();
      this.enabled = true;
      return j;
    } catch { this.enabled = false; return null; }
  },
  async get(path) {
    const r = await fetch(API_BASE + path);
    if (!r.ok) throw new Error('GET '+path+' → '+r.status);
    return r.json();
  },
  async post(path, body) {
    const r = await fetch(API_BASE + path, {
      method: 'POST', headers: {'Content-Type':'application/json'},
      body: JSON.stringify(body || {}),
    });
    if (!r.ok) throw new Error('POST '+path+' → '+r.status);
    return r.json();
  },
  async put(path, body) {
    const r = await fetch(API_BASE + path, {
      method: 'PUT', headers: {'Content-Type':'application/json'},
      body: JSON.stringify(body || {}),
    });
    if (!r.ok) throw new Error('PUT '+path+' → '+r.status);
    return r.json();
  },
};

// ═══════════════════════════════════════════════
// STYLE TOKENS DE TABELA (App.jsx ~lá pelos 800s)
// ═══════════════════════════════════════════════
export const TD  = {padding:'5px 10px', textAlign:'right', fontSize:10.5, fontFamily:'monospace', whiteSpace:'nowrap'};
export const TDl = {...TD, textAlign:'left', fontFamily:'inherit'};
export const TH  = {padding:'7px 10px', textAlign:'right', fontSize:10, fontWeight:700, color:'#756F68', background:'#F4F3F1', whiteSpace:'nowrap'};
export const THl = {...TH, textAlign:'left'};

// ═══════════════════════════════════════════════
// AGENTES_CATEGORIAS (App.jsx)
// ═══════════════════════════════════════════════
export const AGENTES_CATEGORIAS = {
  analise:    {label:'Análise',    cor:'#1d4ed8'},
  ia:         {label:'IA',         cor:'#7C3AED'},
  wizard:     {label:'Wizard',     cor:'#F4A76A'},
  qualidade:  {label:'Qualidade',  cor:'#16a34a'},
  extracao:   {label:'Extração',   cor:'#dc2626'},
};

// ═══════════════════════════════════════════════
// ALUCI-GUARD · padrões + registry SICRO (App.jsx:910–927)
// ═══════════════════════════════════════════════
export const SICRO_REGISTRY = {
  '5910301':'Escavação mecanizada material 1ª categoria',
  '5910302':'Escavação mecanizada material 2ª categoria',
  '5912101':'Aterro compactado',
  '5916101':'Concreto fck 25 MPa',
  '5917101':'Aço CA-50',
  '5921101':'Forma de madeira para concreto',
};
export const SICRO_COBERTURA_AVISO = 'Registry SICRO contém apenas amostra ilustrativa (6 códigos). Em produção, plugar contra SharePoint Manta. Códigos não localizados podem ser válidos — confirme com SICRO oficial DNIT.';
export const ALUCI_PADROES = [
  {tipo:'AL-01_abnt',  nome:'Norma ABNT NBR',  regex:/ABNT\s+NBR\s+(\d{3,5})(?:[-:\s]*(\d{4}))?/gi, severidadeBase:'alta'},
  {tipo:'AL-01_lei',   nome:'Lei federal',     regex:/Lei\s+(?:n[ºo°]?\s*)?(\d{1,5})[\.\/](\d{4})/gi, severidadeBase:'alta'},
  {tipo:'AL-02_url',   nome:'URL',             regex:/https?:\/\/[^\s)'"<>]+/g,                       severidadeBase:'media'},
  {tipo:'AL-02_doi',   nome:'DOI',             regex:/10\.\d{4,9}\/[-._;()/:A-Z0-9]+/gi,             severidadeBase:'baixa'},
  {tipo:'AL-03_sicro', nome:'Código SICRO',    regex:/SICRO\s*[-:]?\s*(\d{7})/gi,                    severidadeBase:'alta'},
];

// ═══════════════════════════════════════════════
// KNOWLEDGE BASE mocks (App.jsx:577–610)
// ═══════════════════════════════════════════════
export const KB_RAG_SUGESTOES = [
  {q:'Qual o CU médio de duplicação nos Lotes PR?',tags:['CU','duplicação','PR']},
  {q:'Quais normas referenciam o BDI de Recuperação de OAE?',tags:['BDI','OAE','norma']},
  {q:'Diferença de BDI entre Lote 3 e Lote 4?',tags:['BDI','L3','L4']},
  {q:'Qual tipologia tem o maior CAPEX agregado nos 3 lotes?',tags:['CAPEX','tipologia']},
  {q:'Quantos km de duplicação foram orçados na BR-376?',tags:['BR-376','duplicação']},
  {q:'Composição SICRO para implantação de OAE em concreto armado',tags:['SICRO','OAE']},
];

export const KB_RAG_MOCK_RESPOSTA = {
  resposta:'O CU médio ponderado de duplicação nos Lotes PR é de aproximadamente R$ 5,58 M/km no Lote 3 (rateio pró-rata do grupo 1.2.1 do MEF PR Vias). Lote 4 e Lote 5 têm quantidades orçadas (227,4 km e 232,7 km respectivamente), mas o CU pelo rateio do agrupamento requer validação — o MEF desses lotes traz apenas o total de grupo sem discriminação por sub-item. A referência DNIT Vol. 10 (doc-008) sugere CU de R$ 5,2-6,1 M/km para duplicações em relevo ondulado no PR, o que valida a ordem de grandeza do Lote 3.',
  fontes:[
    {doc:'doc-004',label:'MEF Lote 3 — PR Vias',trecho:'Grupo 1.2.1 Duplicações · Total R$ 1.278,68 M · Qtd 132,6 km + 99,02 km contornos'},
    {doc:'doc-008',label:'DNIT · Manual de Custos Vol. 10',trecho:'Tab. 4.3 · CU referencial para duplicação em relevo ondulado · PR: R$ 5,2-6,1 M/km'},
    {doc:'doc-015',label:'BDI Referencial ANTT · 2024',trecho:'BDI Construção rodoviária 23,20% (2024) · 23,39% (base 2023)'},
  ],
  confianca:0.86,
};

export const KB_PROMPTS = [
  {id:'p-001',nome:'Análise de CU por tipologia',ativo:true,versao:'v2.1',desc:'Ao receber uma tipologia e contexto geográfico, consultar KB Curada, MEF relevantes e norma DNIT correspondente. Retornar CU com faixa de variação e nível de confiança.',exemplo:'CU de OAE em concreto armado no PR'},
  {id:'p-002',nome:'Comparativo entre lotes',ativo:true,versao:'v1.4',desc:'Comparar quantidades, CUs e CAPEX de uma tipologia entre lotes. Citar sempre a data-base de cada valor. Aplicar fator de atualização quando data-base diferir >6 meses.',exemplo:'Comparar duplicação L3 vs L4 vs L5'},
  {id:'p-003',nome:'Consulta SICRO',ativo:true,versao:'v3.0',desc:'Buscar composição SICRO por código ou descrição. Retornar: insumos, mão-de-obra, equipamentos, CU total. Apontar divergências com MEF quando existirem.',exemplo:'Composição para pavimentação CBUQ faixa C'},
  {id:'p-004',nome:'Justificativa de BDI',ativo:false,versao:'v0.3-draft',desc:'Justificar percentual de BDI adotado com base em normas ANTT, tipo de obra e complexidade. Rascunho — aguardando revisão.',exemplo:'BDI para Recuperação de OAE'},
  {id:'p-005',nome:'Leitura de projeto de engenharia',ativo:true,versao:'v1.0',desc:'Ao ingerir PDF de projeto, extrair: disciplina, estaqueamento, quantidades-chave, OAEs mencionadas, sondagens SPT referenciadas. Popular KB Curada se dado for inédito.',exemplo:'Anteprojeto de duplicação BR-376'},
];

export const KB_INGEST_FILA = [
  {id:'f-001',arquivo:'Anteprojeto_BR376_km213-250.pdf',tam:'18,4 MB',status:'extraindo',progresso:42,etapa:'OCR em 412 páginas',fonte:'SharePoint'},
  {id:'f-002',arquivo:'Laudo_Sondagem_SP01-SP47.pdf',tam:'6,2 MB',status:'indexando',progresso:78,etapa:'Gerando embeddings · 61/89 trechos',fonte:'SharePoint'},
  {id:'f-003',arquivo:'Composicao_SICRO_Pavimentacao.xlsx',tam:'1,8 MB',status:'fila',progresso:0,etapa:'Aguardando slot',fonte:'Upload manual'},
  {id:'f-004',arquivo:'Edital_ANTT_Aditivo_02.pdf',tam:'2,3 MB',status:'curado',progresso:100,etapa:'Ingestão concluída · 12 composições novas',fonte:'SharePoint'},
  {id:'f-005',arquivo:'Planta_Geom_L4_contorno_norte.dwg',tam:'42,1 MB',status:'erro',progresso:15,etapa:'Formato DWG: requer conversor · adicionar ao backlog',fonte:'SharePoint'},
];

// ═══════════════════════════════════════════════
// META_OFICIAL (App.jsx:669)
// ═══════════════════════════════════════════════
export const META_OFICIAL = {
  '3': {uf:'PR', cidade:'Centro-oeste · entre Cascavel e Toledo', ctxRural:0.65, ctxUrbano:0.20, ctxSerra:0.15},
  '4': {uf:'PR', cidade:'Noroeste · Maringá-Umuarama', ctxRural:0.55, ctxUrbano:0.30, ctxSerra:0.15},
  '5': {uf:'PR', cidade:'Norte/sinuoso · Cornélio Procópio-Apucarana', ctxRural:0.60, ctxUrbano:0.10, ctxSerra:0.30},
};

// ═══════════════════════════════════════════════
// localStorage helpers (App.jsx:679,687)
// ═══════════════════════════════════════════════
const KB_EXTRAS_KEY = 'manta-kb-extras-v1';
const ORC_GERADOS_KEY = 'manta-orcamentos-gerados-v1';
export function saveKbExtras(arr) {
  if (typeof window === 'undefined') return;
  try { localStorage.setItem(KB_EXTRAS_KEY, JSON.stringify(arr)); } catch(e){}
}
export function loadKbExtras() {
  if (typeof window === 'undefined') return [];
  try { return JSON.parse(localStorage.getItem(KB_EXTRAS_KEY) || '[]'); } catch(e){ return []; }
}
export function loadOrcGerados() {
  if (typeof window === 'undefined') return [];
  try { return JSON.parse(localStorage.getItem(ORC_GERADOS_KEY) || '[]'); } catch(e){ return []; }
}
export function saveOrcGerados(arr) {
  if (typeof window === 'undefined') return;
  try { localStorage.setItem(ORC_GERADOS_KEY, JSON.stringify(arr)); } catch(e){}
}

// ═══════════════════════════════════════════════
// FONTE_KPI_STYLES (App.jsx:799)
// ═══════════════════════════════════════════════
export const FONTE_KPI_STYLES = {
  manual:   {bg:'#F3F4F6', fg:'#374151', label:'manual'},
  preset:   {bg:'#FEF3C7', fg:'#92400E', label:'preset'},
  import:   {bg:'#DBEAFE', fg:'#1E40AF', label:'import'},
  extraido: {bg:'#D1FAE5', fg:'#065F46', label:'extraído'},
};

// ═══════════════════════════════════════════════
// SHAREPOINT probe (App.jsx:732)
// ═══════════════════════════════════════════════
export function probeSharePointBackend(setBackendStatus) {
  SP_API.probe().then(r => setBackendStatus(r.ok ? 'on' : 'off'));
}

// ═══════════════════════════════════════════════
// EXTRACTOR VISUAL · paletas e layout (App.jsx:1009–1020)
// ═══════════════════════════════════════════════
export const PAL_OAE = {c:"#9333ea", a:"OAE", l:"OAE (ponte/viaduto)"};
export const PAL_SEG = {duplicacao:"#8B4A2D", faixasAdicionais:"#D4682A", marginais:"#4B5563", contornos:"#2057A8", ciclovias:"#1B7A50"};
export const LBL_SEG = {duplicacao:"Duplicação", faixasAdicionais:"Faixa adicional", marginais:"Marginal", contornos:"Contorno", ciclovias:"Ciclovia"};
export const SEG_STACK = ["duplicacao","faixasAdicionais","marginais","contornos","ciclovias"];
export const DEV_ORDER = Object.keys(PAL_DEV);
export const LBL_GAP = {roadType:"tipo de pista", duplicationSide:"lado de duplicação", barrier:"barreira", existingLighting:"iluminação existente", poles:"postes"};
export const RY = {ruler:8, rulerH:24, chipA:70, chipB:110, chipR:12, roadTop:148, roadH:56, roadMid:176, oae:216, segStart:246, segH:11, segGap:16, total:340, gutter:102, inset:10, rightPad:12};
export const segYPos = k => RY.segStart + SEG_STACK.indexOf(k) * RY.segGap;
export const PAD_L = RY.gutter + RY.inset;
